import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Simplified fault detection hook that now gets fault data from useRealTimeData
 * No longer polls separately - just processes fault data from the combined endpoint
 */
export const useFaultDetection = (faultsData = []) => {
  // React 19: Consolidated state management
  const [state, setState] = useState({
    faults: [],
    loading: false,
    error: null,
    hasFaults: false,
  })
  
  const mountedRef = useRef(true)
  
  // ES2022+: Simplified fault processing
  const processFaults = useCallback((rawFaults) => {
    if (!rawFaults?.length) return []
    return rawFaults.map(fault => ({
      device: fault.device,
      measurement: fault.measurement,
      value: fault.value || 'fault detected',
      timestamp: fault.timestamp || new Date().toISOString(),
      severity: fault.severity || 'error',
    }))
  }, [])
  
  // Process faults whenever faultsData changes
  useEffect(() => {
    if (mountedRef.current) {
      const processedFaults = processFaults(faultsData)
      setState(prev => ({
        ...prev,
        faults: processedFaults,
        hasFaults: processedFaults.length > 0,
        error: null,
        loading: false,
      }))
    }
  }, [faultsData, processFaults])
  
  // React 19: Simplified effect management
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  
  // Utility functions with consolidated state
  const refresh = useCallback(() => {
    // Refresh is now handled by the parent component calling useRealTimeData
    setState(prev => ({ ...prev, loading: true, error: null }))
  }, [])
  
  const clearFaults = useCallback(() => {
    setState(prev => ({ ...prev, faults: [], hasFaults: false }))
  }, [])
  
  // Memoized derived state
  const memoizedState = useMemo(() => ({
    ...state,
    refresh,
    clearFaults,
  }), [state, refresh, clearFaults])
  
  return memoizedState
}
